import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { mapResponse } from '@ngrx/operators';
import { Action } from '@ngrx/store';
import { retryUnauthorizedAction } from '@state/authentication/authentication.actions';
import { MessageService } from 'primeng/api';

interface EffectHandler<PayloadType, SuccessResponseType = Action, FailureResponseType = Action> {
  success: SuccessResponseType | ((response: PayloadType) => SuccessResponseType);
  retry: Action;
  failure: FailureResponseType | ((error: HttpErrorResponse) => FailureResponseType);
  successMessage?: string;
  failureMessage?: string;
  showErrorDetail?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class EffectHandlerService {
  private msg = inject(MessageService);


  /**
   * Function to handle the response to an action triggered in an effect and response accordingly to success and failure
   * @param success - Arrow function which is called on success and returns an {@link Action}
   * @param retry - Action to retry if there is a 401 Unauthorized error
   * @param failure - Action to complete on failure.
   * @param successMessage (optional) - Message to display on success
   * @param failureMessage (optional) - Message to display on failure
   * @param showErrorDetail (optional) - Show the detail of the error response
   */
  effectResponse<PayloadType, SuccessResponseType = Action, FailureResponseType = Action>({
    success,
    retry,
    failure,
    successMessage,
    failureMessage,
    showErrorDetail
  }: EffectHandler<PayloadType, SuccessResponseType, FailureResponseType>) {
    return mapResponse({
      next: (response: PayloadType) => {
        if (successMessage) {
          this.successMessage(successMessage);
        }
        if (success instanceof Function) {
          return success(response);
        } else {
          return success;
        }
      },
      error: (error: HttpErrorResponse) => {
        if (error.status === 401) {
          return retryUnauthorizedAction(retry);
        } else {
          if (failureMessage) {
            this.errorMessage(failureMessage + (showErrorDetail ? ': ' + error.statusText : ''));
          }
          console.error(error);

          if (failure instanceof Function) {
            return failure(error);
          } else {
            return failure;
          }
        }
      }
    });
  }

  /**
   * Display message
   * @param detail
   * @param summary
   * @param severity
   * @param icon
   * @param life
   */
  displayMessage({ detail, summary, severity, icon, life = 4000 }: {
    detail: string,
    summary: string,
    severity: 'success' | 'error',
    life?: number,
    icon?: string,
  }) {
    this.msg.add({
      detail,
      summary,
      life,
      severity,
      icon,
    });
  }

  errorMessage(detail: string) {
    this.displayMessage({
      detail,
      summary: 'Error',
      severity: 'error',
      life: 8000,
    });
  }

  successMessage(detail: string) {
    this.displayMessage({
      detail,
      summary: 'Success',
      severity: 'success',
    });
  }
}
